import { Link, useParams } from "react-router-dom"
import { useMemo } from "react"
import { SEO } from "@/components/SEO"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { ChevronLeft, ChevronRight, ExternalLink } from "lucide-react"

export function DocumentationPage() {
  const { section } = useParams()

  const pages = useMemo(
    () => [
      {
        slug: "getting-started",
        title: "Getting started",
        description: "How to set up your workspace, roles, and your first org structure.",
        content: (
          <>
            <h1 className="text-3xl font-bold tracking-tight">Getting started</h1>
            <p className="mt-2 text-muted-foreground">
              A quick path to go live without chaos. This guide is intentionally minimal.
            </p>

            <div className="mt-8 space-y-8">
              <section>
                <h2 className="text-xl font-semibold">1. Create your organization</h2>
                <p className="mt-2 text-muted-foreground">
                  Add company profile, locations, departments and designations. Start with a clean org structure.
                </p>
              </section>
              <section>
                <h2 className="text-xl font-semibold">2. Define policies</h2>
                <p className="mt-2 text-muted-foreground">
                  Configure attendance rules, leave policies, and approval flows. Keep it simple first, refine later.
                </p>
              </section>
              <section>
                <h2 className="text-xl font-semibold">3. Migrate employees</h2>
                <p className="mt-2 text-muted-foreground">
                  Import employees in phases (pilot → full org). Validate data and roles before enabling payroll.
                </p>
              </section>
            </div>
          </>
        ),
      },
      {
        slug: "product",
        title: "Product documentation",
        description: "Core product concepts, modules, and recommended rollout sequence.",
        content: (
          <>
            <h1 className="text-3xl font-bold tracking-tight">Product documentation</h1>
            <p className="mt-2 text-muted-foreground">
              A structured view of the platform—what it does, and how teams typically adopt it.
            </p>
            <div className="mt-8 grid gap-4 md:grid-cols-2">
              {[
                ["Core HR", "/product/core-hr"],
                ["Attendance & Leave", "/product/attendance-leave"],
                ["Payroll & Compliance", "/product/payroll"],
                ["Performance", "/product/performance"],
                ["Recruitment / ATS", "/product/recruitment"],
                ["Analytics", "/product/analytics"],
              ].map(([label, href]) => (
                <Link
                  key={href}
                  to={href}
                  className="group rounded-xl border bg-card p-4 shadow-sm transition-colors hover:bg-muted/40"
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{label}</div>
                    <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-foreground" />
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground">View module overview</div>
                </Link>
              ))}
            </div>
          </>
        ),
      },
      {
        slug: "api",
        title: "API documentation",
        description: "Authentication, endpoints, webhooks, and rate limits (placeholder).",
        content: (
          <>
            <h1 className="text-3xl font-bold tracking-tight">API documentation</h1>
            <p className="mt-2 text-muted-foreground">
              REST API reference and integration patterns. Replace these placeholders with your real endpoints.
            </p>

            <div className="mt-8 space-y-8">
              <section>
                <h2 className="text-xl font-semibold">Authentication</h2>
                <p className="mt-2 text-muted-foreground">
                  Use API keys / OAuth (as applicable). Rotate keys and scope access.
                </p>
                <pre className="mt-4 overflow-auto rounded-xl border bg-muted/40 p-4 text-sm">
{`curl -H "Authorization: Bearer <token>" \\
  https://api.yourdomain.com/v1/employees`}
                </pre>
              </section>
              <section>
                <h2 className="text-xl font-semibold">Webhooks</h2>
                <p className="mt-2 text-muted-foreground">
                  Subscribe to events like employee.created, payroll.completed, leave.approved.
                </p>
              </section>
            </div>
          </>
        ),
      },
      {
        slug: "integrations",
        title: "Integration guides",
        description: "SSO, HRIS imports, payroll integrations, and custom APIs.",
        content: (
          <>
            <h1 className="text-3xl font-bold tracking-tight">Integration guides</h1>
            <p className="mt-2 text-muted-foreground">
              Connect Nestor to your identity provider, accounting, payroll, and internal tools.
            </p>
            <div className="mt-8 space-y-6">
              <section>
                <h2 className="text-xl font-semibold">SSO (SAML / OIDC)</h2>
                <p className="mt-2 text-muted-foreground">
                  Configure SSO for secure access, SCIM for provisioning (optional).
                </p>
              </section>
              <section>
                <h2 className="text-xl font-semibold">Custom integrations</h2>
                <p className="mt-2 text-muted-foreground">
                  Use APIs + webhooks for near real-time sync. Keep integrations stateless and idempotent.
                </p>
              </section>
            </div>
          </>
        ),
      },
      {
        slug: "user-guides",
        title: "User guides",
        description: "Step-by-step flows for employees and managers.",
        content: (
          <>
            <h1 className="text-3xl font-bold tracking-tight">User guides</h1>
            <p className="mt-2 text-muted-foreground">
              Simple flows that reduce support tickets.
            </p>
            <div className="mt-8 space-y-6">
              <section>
                <h2 className="text-xl font-semibold">Apply leave</h2>
                <ol className="mt-2 list-decimal pl-5 text-muted-foreground space-y-1">
                  <li>Open Leave</li>
                  <li>Select dates</li>
                  <li>Add reason</li>
                  <li>Submit for approval</li>
                </ol>
              </section>
              <section>
                <h2 className="text-xl font-semibold">Download payslip</h2>
                <ol className="mt-2 list-decimal pl-5 text-muted-foreground space-y-1">
                  <li>Go to Payroll</li>
                  <li>Select month</li>
                  <li>Download PDF</li>
                </ol>
              </section>
            </div>
          </>
        ),
      },
      {
        slug: "faq",
        title: "FAQ",
        description: "Common questions about billing, security, and rollout.",
        content: (
          <>
            <h1 className="text-3xl font-bold tracking-tight">FAQ</h1>
            <p className="mt-2 text-muted-foreground">
              Short answers, no fluff.
            </p>
            <div className="mt-8 space-y-6">
              <section>
                <h2 className="text-xl font-semibold">Is this GDPR / DPDP compliant?</h2>
                <p className="mt-2 text-muted-foreground">
                  Yes (see Privacy + DPA pages). Full compliance depends on your configuration and data handling policies.
                </p>
              </section>
              <section>
                <h2 className="text-xl font-semibold">Can we migrate from our current HRMS?</h2>
                <p className="mt-2 text-muted-foreground">
                  Yes. We support phased migration with validation and rollback strategy.
                </p>
              </section>
            </div>
          </>
        ),
      },
      {
        slug: "changelog",
        title: "Changelog",
        description: "Release notes and platform updates (placeholder).",
        content: (
          <>
            <h1 className="text-3xl font-bold tracking-tight">Changelog</h1>
            <p className="mt-2 text-muted-foreground">
              What shipped recently.
            </p>
            <div className="mt-8 space-y-6">
              <div className="rounded-xl border bg-card p-5 shadow-sm">
                <div className="text-sm font-semibold">2026-01-24</div>
                <div className="mt-2 text-muted-foreground">
                  Improvements to performance pages, docs navigation, and marketing UI polish.
                </div>
              </div>
              <div className="rounded-xl border bg-card p-5 shadow-sm">
                <div className="text-sm font-semibold">2026-01-10</div>
                <div className="mt-2 text-muted-foreground">
                  Added onboarding workflows and candidate portal enhancements.
                </div>
              </div>
            </div>
          </>
        ),
      },
    ],
    []
  )

  const activeSlug = section || "getting-started"
  const activeIndex = Math.max(
    0,
    pages.findIndex((p) => p.slug === activeSlug)
  )
  const active = pages[activeIndex] || pages[0]
  const prev = pages[activeIndex - 1]
  const next = pages[activeIndex + 1]

  return (
    <>
      <SEO
        title={`${active.title} - Documentation`}
        description={active.description}
      />

      <section className="bg-gradient-to-b from-primary/10 via-background to-background py-10">
        <div className="container">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-muted-foreground">Documentation</div>
              <div className="mt-1 text-xl font-semibold tracking-tight">{active.title}</div>
            </div>
            <Button variant="outline" className="rounded-full" asChild>
              <Link to="/contact">Talk to Sales</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="container py-10">
        <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
          {/* Left sidebar */}
          <aside className="lg:sticky lg:top-20 lg:h-[calc(100vh-96px)] lg:overflow-auto">
            <div className="rounded-2xl border bg-card p-3 shadow-sm">
              <div className="px-2 pb-2 pt-1 text-xs font-semibold text-muted-foreground">
                Navigation
              </div>
              <nav className="grid gap-1">
                {pages.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/docs/${p.slug}`}
                    className={cn(
                      "rounded-xl px-3 py-2 text-sm transition-colors",
                      p.slug === active.slug
                        ? "bg-accent text-foreground"
                        : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                    )}
                  >
                    {p.title}
                  </Link>
                ))}
              </nav>
            </div>
          </aside>

          {/* Right content */}
          <article className="min-w-0">
            <div className="rounded-2xl border bg-card p-6 shadow-sm md:p-10">
              <div className="prose prose-slate max-w-none">
                {active.content}
              </div>

              <div className="mt-10 flex flex-col gap-3 border-t pt-6 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  {prev ? (
                    <Button variant="outline" className="rounded-full" asChild>
                      <Link to={`/docs/${prev.slug}`}>
                        <ChevronLeft className="mr-2 h-4 w-4" />
                        Previous
                      </Link>
                    </Button>
                  ) : (
                    <div />
                  )}
                </div>
                <div className="text-sm text-muted-foreground">
                  {activeIndex + 1} / {pages.length}
                </div>
                <div className="sm:text-right">
                  {next ? (
                    <Button className="rounded-full" asChild>
                      <Link to={`/docs/${next.slug}`}>
                        Next
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  ) : (
                    <div />
                  )}
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>
    </>
  )
}
